<script setup lang="ts">
  import { computed, ref, toRefs, useSlots } from 'vue';
  import { notificationDate } from '@/common/utils/formatter.date';
  import {
    NOTIFICATION_ICONS,
    NotificationType
  } from '@/common/constants/notifications.constants';
  import { ElNotificationOptions } from '@uikit/ui-kit/types/notification';
  import { store } from '@/common/root';
  import useNotification from '@/common/composables/useNotification';
  import More from '@/common/shared/More.vue';

  type Props = {
    centerIcon?: boolean,
    notification: Required<ElNotificationOptions>,
    color?: string,
    currentTab: 'notifications' | 'tasks',
  }

  const props = defineProps<Props>();

  const {
    color,
    notification,
    currentTab,
    centerIcon,
  } = toRefs(props);

  const slots = useSlots();

  const icon = computed(() => NOTIFICATION_ICONS[notification.value.type]);

  const withProgressBar = computed(() => notification.value.progress !== undefined
    && notification.value.progress !== null
    && currentTab.value !== 'tasks'
  );

  const moreRef = ref<InstanceType<typeof More> | null>(null);

  const textRowCount = computed(() => moreRef.value?.textRowCount || 0);

  const { moreThanMaxRows, actionButtons } = useNotification({
    notification,
    textRowCount,
  });

  const routeActions = computed(() => actionButtons.value?.filter((action) => 'route' in action) || []);
  const clickActions = computed(() => actionButtons.value?.filter(action => !('route' in action)) || []);

  const showActions = computed(() => (actionButtons.value.length || !!slots.actions) && currentTab.value !== 'tasks');
  const isProgressType = computed(() => notification.value.type === NotificationType.PROGRESS);

  const remove = () => {
    store.dispatch('notifications/REMOVE', notification.value.id);
  };

  defineExpose({
    remove,
    withProgressBar
  });
</script>

<template>
<div data-component-name="notification"
  class="notification"
  :class="{ [`color_${color}`]: true }"
  :data-operationid="notification.operationId"
  :data-testid="notification.type"
>
  <div class="notification-body">
    <div
      v-if="!withProgressBar && currentTab"
      class="notification-icon"
      :class="{ 'align-center': centerIcon }"
    >
      <slot name="icon">
        <el-loading data-component-name="el-loading" v-if="notification.type === NotificationType.PROGRESS" />
        <el-icon data-component-name="el-icon"
          v-else
          :color="icon.color"
          :name="icon.name"
        />
      </slot>
    </div>
    <div class="notification-content">
      <slot>
        <div class="progress">
          <div
            v-if="notification.title"
            class="title"
            :title="notification.title"
            :class="{ 'shrink-width': !isProgressType }"
          >
            {{ notification.title }}
          </div>
          <div
            v-if="withProgressBar"
            class="my-8"
          >
            <el-progress data-component-name="el-progress"
              animate
              :percentage="notification.progress"
            />
          </div>
          <div
            v-if="notification.message"
            :class="{ 'shrink-width': !isProgressType && !notification.title }"
          >
            <more
              ref="moreRef"
              :text="notification.message"
              :hide-toggle="moreThanMaxRows"
            />
          </div>
        </div>
        <div
          v-if="!isProgressType"
          class="date"
        >
          {{ notificationDate(notification.timestamp) }}
        </div>
      </slot>
      <div
        v-if="showActions"
        class="actions"
      >
        <slot name="actions">
          <el-button data-component-name="el-button"
            v-for="actionButton in clickActions"
            :key="actionButton.label"
            type="ghost"
            @click="actionButton.click"
          >
            {{ actionButton.label }}
          </el-button>
          <router-link data-component-name="router-link"
            v-for="actionButton in routeActions"
            :key="actionButton.label"
            v-slot="{ navigate }"
            :to="actionButton.route"
            custom
          >
            <el-button data-component-name="el-button"
              type="ghost"
              @click="navigate"
            >
              {{ actionButton.label }}
            </el-button>
          </router-link>
        </slot>
      </div>
    </div>
    <div
      v-if="!isProgressType"
      class="notification-close"
    >
      <el-button data-component-name="el-button"
        class="notification-close-button"
        icon="times"
        type="ghost"
        @click="remove"
      />
    </div>
  </div>
</div>
</template>

<style scoped lang="scss">
.notification-close-button {
  display: none;
}

.notification {
  display: flex;
  margin-bottom: 16px;
  padding: 16px 24px;
  border: $av-border-sm $av-brand-light;
  border-radius: $av-border-radius-2;
  cursor: pointer;
  flex-direction: column;

  &:last-child {
    margin-bottom: 0;
  }

  &.color_white {
    background: $av-inversed-primary;
  }

  &.color_brand-secondary-accent {
    background: $av-solid-brand-secondary-accent;
  }

  &:hover {
    border-color: $av-brand-primary;

    .notification-close-button {
      display: inline-block;
    }

    .date {
      visibility: hidden;
    }
  }
}

.notification-body {
  max-width: 100%;
  position: relative;
  padding: 0;
  flex: 1 auto;
  min-width: 0;
  display: flex;

  .notification-content {
    flex: 1;
    min-width: 0;
  }

  .progress {
    flex: 1;
    overflow: hidden;
  }

  .title {
    font-weight: $av-font-weight-bold;
    max-width: 100%;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

  .shrink-width {
    max-width: 80%;
  }

  .date {
    position: absolute;
    right: 0;
    top: 0;
    font-size: $av-font-size-xs;
    color: $av-fixed-light;
  }
}

.notification-icon {
  display: flex;
  padding-right: 24px;
  padding-top: 4px;
}

.align-center {
  align-self: center;
}

.notification-close {
  position: absolute;
  z-index: $av-z-index-normal;
  right: -16px;
  top: -4px;
}

.actions {
  margin-left: -8px;
  margin-top: 8px;
}

</style>
